import {useState} from 'react';

import {Button, Input, Modal, MultiSelectDropdown, Select} from '@/components';
import {getCountryOptions} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {CreateSubscriptionPlanDTO} from '@/services/subscriptionPlanService';
import type {AfriexCountryCode} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import {mapCountryListToOptions} from '@/utils/countries';
import {formatSubscriptionPlanData} from '@/utils/subscriptionPlan';

import type {CreatePlanModalProps} from './types';

export default function CreatePlanModal({
  isOpen,
  label,
  onClose,
  errors,
  loading,
  onSubmit,
  subPlanFeatures,
  subPlanIntervals,
}: CreatePlanModalProps & {
  onSubmit: (form: CreateSubscriptionPlanDTO) => Promise<void>;
}) {
  const [form, setForm] = useState<CreateSubscriptionPlanDTO>({
    name: '',
    description: '',
    amount: 1,
    currency: AfriexCurrencyCode.USD,
    countries: [],
    interval: '',
    features: [],
    isActive: false,
  });
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const countryOptions = mapCountryListToOptions(countryList);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const {target} = e;
    const {name, value} = target;
    if (name === 'features') {
      setForm((prev) => ({
        ...prev,
        features: [value],
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await onSubmit(form);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title={label}
      closeOnOutsideClick
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {[
          {name: 'name', type: 'text', label: 'Plan Name', component: Input},
          {
            name: 'description',
            type: 'text',
            label: 'Description',
            component: Input,
          },
          {
            name: 'amount',
            type: 'number',
            label: 'Amount (USD)',
            component: Input,
          },
        ].map(({name, type, label, component: Component}) => (
          <div key={name}>
            <Component
              label={label}
              type={type}
              name={name}
              value={form[name as keyof typeof form] as string | number}
              onChange={handleChange}
              className="w-full"
              placeholder={`Enter ${label}`}
              error={errors[name as keyof typeof errors]}
            />
          </div>
        ))}

        {/* Interval */}
        <div>
          <Select
            label="Interval"
            name="interval"
            value={form.interval}
            onChange={handleChange}
            className="w-full"
            options={Object.values(subPlanIntervals).map((val) => ({
              key: formatSubscriptionPlanData(val),
              value: val,
            }))}
            placeholder="Select Interval"
            error={errors.interval}
          />
        </div>

        {/* Features */}
        <div className="">
          <Select
            label="Features"
            name="features"
            value={form.features}
            onChange={handleChange}
            className="w-full"
            options={Object.values(subPlanFeatures).map((val) => ({
              key: formatSubscriptionPlanData(val),
              value: val,
            }))}
            placeholder="Select feature"
            error={errors.features}
          />
        </div>

        {/* Countries */}
        <div className="">
          <MultiSelectDropdown
            label="Countries"
            name="countries"
            onChange={(p: unknown[]) => {
              const countries = p as AfriexCountryCode[];
              setForm((prev) => ({
                ...prev,
                countries,
              }));
            }}
            options={getCountryOptions(countryOptions)}
            prompt="Select countries"
            error={errors.countries}
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Create Plan'}
        </Button>
      </form>
    </Modal>
  );
}
